import { useCallback, useRef, useState } from 'react';
import type { ChangeEvent, DragEvent } from 'react';
import CloudUploadRoundedIcon from '@mui/icons-material/CloudUploadRounded';
import type { FileUploadDropzoneProps } from './FileUploadDropzone.props';
import {
  FileUploadDropzoneHelperText,
  FileUploadDropzoneIconFrame,
  FileUploadDropzoneInput,
  FileUploadDropzoneRoot,
  FileUploadDropzoneSelectedFile,
  FileUploadDropzoneSelectedFileMeta,
  FileUploadDropzoneSelectedFileName,
  FileUploadDropzoneStrongText,
  FileUploadDropzoneText,
} from './FileUploadDropzone.styles';

const getFirstFile = (files: FileList | null | undefined) => files?.[0] ?? null;

const FileUploadDropzone = ({
  accept,
  disabled = false,
  file = null,
  helperText = '(15 MB max file size.)',
  inputTestId,
  multiple = false,
  onFileChange,
  onFilesChange,
  primaryText = 'Drag & Drop Supporting Documents',
  secondaryText = 'Upload supporting documents',
  supportingText = 'or Select a file',
  testId,
}: FileUploadDropzoneProps) => {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [isDragActive, setIsDragActive] = useState(false);

  const handleFiles = useCallback((files: FileList | null | undefined) => {
    if (disabled) {
      return;
    }

    const selectedFiles = Array.from(files ?? []);

    if (onFilesChange) {
      onFilesChange(selectedFiles);
      return;
    }

    onFileChange?.(getFirstFile(files));
  }, [disabled, onFileChange, onFilesChange]);

  const handleInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    const input = event.currentTarget;

    handleFiles(event.target.files);
    input.value = '';
  };

  const handleDragEvent = (event: DragEvent<HTMLButtonElement>) => {
    event.preventDefault();
    event.stopPropagation();
  };

  const handleDragEnter = (event: DragEvent<HTMLButtonElement>) => {
    handleDragEvent(event);

    if (!disabled) {
      setIsDragActive(true);
    }
  };

  const handleDragLeave = (event: DragEvent<HTMLButtonElement>) => {
    handleDragEvent(event);
    setIsDragActive(false);
  };

  const handleDrop = (event: DragEvent<HTMLButtonElement>) => {
    handleDragEvent(event);
    setIsDragActive(false);
    handleFiles(event.dataTransfer.files);
  };

  return (
    <FileUploadDropzoneRoot
      aria-label={multiple ? 'Select documents to upload' : 'Select a document to upload'}
      data-testid={testId}
      disabled={disabled}
      isDragActive={isDragActive}
      onClick={() => inputRef.current?.click()}
      onDragEnter={handleDragEnter}
      onDragLeave={handleDragLeave}
      onDragOver={handleDragEvent}
      onDrop={handleDrop}
      type="button">
      <FileUploadDropzoneInput
        ref={inputRef}
        accept={accept}
        data-testid={inputTestId}
        disabled={disabled}
        multiple={multiple}
        onChange={handleInputChange}
        type="file"
      />
      <FileUploadDropzoneIconFrame data-file-upload-dropzone-icon="true">
        <CloudUploadRoundedIcon fontSize="inherit" />
      </FileUploadDropzoneIconFrame>
      {secondaryText ? <FileUploadDropzoneText>{secondaryText}</FileUploadDropzoneText> : null}
      {primaryText ? <FileUploadDropzoneStrongText>{primaryText}</FileUploadDropzoneStrongText> : null}
      {supportingText ? <FileUploadDropzoneStrongText>{supportingText}</FileUploadDropzoneStrongText> : null}
      {helperText ? (
        <FileUploadDropzoneHelperText>{helperText}</FileUploadDropzoneHelperText>
      ) : null}
      {file ? (
        <FileUploadDropzoneSelectedFile>
          <FileUploadDropzoneSelectedFileName>{file.name}</FileUploadDropzoneSelectedFileName>
          <FileUploadDropzoneSelectedFileMeta>Ready to upload</FileUploadDropzoneSelectedFileMeta>
        </FileUploadDropzoneSelectedFile>
      ) : null}
    </FileUploadDropzoneRoot>
  );
};

export default FileUploadDropzone;
